import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { NumberParam, StringParam, useQueryParams } from 'use-query-params'
import { Button, SearchComponent, Select, StartPage } from '../../../components'
import ReleaseDedicatedPreorder from '../../../components/modal-alerts/release-dedicated-preorder'
import dedicatedPreordersService, { DedicatedPreorder, DedicatedPreorderStatus } from '../../../api/dedicatedPreordersService'
import { usePageTitle } from '../../../context/navigationContext'
import { useUi } from '../../../context/ui.context'
import { formatDate } from '../../../utils/date-formatter'
import { icons } from '../../../icons'
import { Filters, FilterSelect, SearchField, SearchRow } from '../styles'
import { HardwareListPage } from '../preorder-hardware/styles'
import { canReleasePreorder, diskDescriptions, quickPreorderStatuses, preorderError, preordersPath, preorderStatuses, preorderStatusKey } from './helpers'
import usePolledData from './usePolledData'
import { Configuration, PreorderBadge, ResourceLink } from './styles'

const DedicatedPreordersPage: FC = () => {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const { viewAlert } = useUi()
  const [query, setQuery] = useQueryParams({
    page: NumberParam, page_size: NumberParam, ordering: StringParam, search: StringParam,
    status: StringParam, dedicated_user: StringParam, issued_server: StringParam,
  })
  const { page, page_size: pageSize, ordering, search, status, dedicated_user: user, issued_server: server } = query
  const previousSearch = useRef(search)
  const requestPage = previousSearch.current === search ? Math.max(1, page || 1) : 1
  const requestPageSize = Math.max(1, pageSize || 10)
  useEffect(() => {
    if (previousSearch.current !== search) {
      previousSearch.current = search
      setQuery({ page: 1 }, 'replaceIn')
    }
  }, [search, setQuery])
  const [releaseTarget, setReleaseTarget] = useState<DedicatedPreorder>()
  const request = useCallback(async (signal: AbortSignal) => {
    const result = await dedicatedPreordersService.list({
      page: requestPage, page_size: requestPageSize, ordering: ordering || undefined, search: search || undefined,
      status: status || undefined, dedicated_user: user || undefined, issued_server: server || undefined,
    }, signal)
    const lastPage = Math.max(1, Math.ceil(result.count / requestPageSize))
    if (!signal.aborted && requestPage > lastPage) setQuery({ page: lastPage }, 'replaceIn')
    return result
  }, [requestPage, requestPageSize, ordering, search, status, user, server, setQuery])
  const { data, loading, error, reload } = usePolledData(request)
  usePageTitle(t('PREORDERS_TITLE'))

  const changeStatus = (item: DedicatedPreorder, nextStatus: DedicatedPreorderStatus) => {
    dedicatedPreordersService.update(item.id, { status: nextStatus })
      .then(() => {
        viewAlert({ severity: 'success', message: t('PREORDERS_STATUS_UPDATED') })
        reload()
      })
      .catch((err) => viewAlert({ severity: 'error', message: preorderError(err, String(t('ERROR'))) }))
  }

  const openRelease = (item: DedicatedPreorder) => setReleaseTarget(data?.objects.find((preorder) => preorder.id === item.id) || item)

  // Released goes through the release modal, where a server is picked.
  const statusOptions = quickPreorderStatuses.map((value) => ({
    id: `status-${value}`,
    title: t('PREORDERS_SET_STATUS', { status: t(preorderStatusKey(value)) }),
    icon: icons.Restart,
    divider: false,
    hidden: (item: DedicatedPreorder) => (
      value === 'released' ? !canReleasePreorder(item) : item.status === value || item.status === 'released'
    ),
    onClick: (item: DedicatedPreorder) => (value === 'released' ? openRelease(item) : changeStatus(item, value)),
  }))

  const rows = (data?.objects || []).map((preorder) => ({
    ...preorder,
    name: preorder.name || preorder.id,
    user: preorder.dedicated_user ? <ResourceLink to={`/dedicated-servers/users/${preorder.dedicated_user.id}`}>#{preorder.dedicated_user.external_id}</ResourceLink> : '-',
    configuration: (
      <Configuration>
        <span>CPU: {preorder.cpu ? `${preorder.cpu_count} × ${preorder.cpu.name || preorder.cpu.model}` : '-'}</span>
        <span>{t('RAM')}: {preorder.ram ? `${preorder.ram_count} × ${preorder.ram.size_mb} MB` : '-'}</span>
        {diskDescriptions(preorder).map((disk, index) => <span key={index}>{disk}</span>)}
      </Configuration>
    ),
    status_display: <PreorderBadge $status={preorder.status}>{t(preorderStatusKey(preorder.status))}</PreorderBadge>,
    server: preorder.issued_server ? <ResourceLink to={`/dedicated-servers/servers/${preorder.issued_server.id}`}>{preorder.issued_server.name || preorder.issued_server.hostname || `#${preorder.issued_server.external_id}`}</ResourceLink> : '-',
    created_at: formatDate(preorder.created_at),
    navigate: `${preordersPath}/${preorder.id}`,
  }))

  const currentTarget = releaseTarget && (data?.objects.find((item) => item.id === releaseTarget.id) || releaseTarget)

  return (
    <HardwareListPage data-testid="dedicated-preorders-page">
      <StartPage
        title={t('PREORDERS_TITLE')}
        haveResource={rows.length > 0}
        noResourceText={error ? preorderError(error, String(t('PREORDERS_LOAD_FAILED'))) : String(t('PREORDERS_EMPTY'))}
        pageLoading={loading}
        withoutCreateResourceButton
        rightCustomCreateButton={(
          <Filters>
            <SearchRow>
              <SearchField><SearchComponent /></SearchField>
              <FilterSelect>
                <Select isSmall title={t('STATUS')} selectedValue={status || ''}
                  data={[{ value: '', label: t('ALL') }, ...preorderStatuses.map((value) => ({ value, label: t(preorderStatusKey(value)) }))]}
                  onChange={(value) => setQuery({ status: value || undefined, page: 1 })} />
              </FilterSelect>
              {(search || status || user || server) && <Button variant="stroke" size="display" title={t('CLEAR_FILTERS')} onClick={() => setQuery({ search: undefined, status: undefined, dedicated_user: undefined, issued_server: undefined, page: 1 })} />}
            </SearchRow>
          </Filters>
        )}
        tableData={{
          columns: [
            { title: t('NAME'), key: 'name', sorted: true },
            { title: t('DEDICATED_USER'), key: 'user' },
            { title: t('DEDICATED_CONFIGURATION'), key: 'configuration' },
            { title: t('STATUS'), key: 'status_display' },
            { title: t('PREORDERS_ISSUED_SERVER'), key: 'server' },
            { title: t('CREATED_AT'), key: 'created_at', sorted: true },
            { title: '', key: 'props' },
          ],
          data: rows, paginationData: data, hideCheckbox: true, showSearchSkeleton: false,
          props: [
            { id: 'edit', title: t('EDIT'), icon: icons.Edit, onClick: (item: DedicatedPreorder) => navigate(`${preordersPath}/${item.id}/edit`) },
            ...statusOptions,
          ],
        }}
      />
      {currentTarget && <ReleaseDedicatedPreorder preorder={currentTarget} onClose={() => setReleaseTarget(undefined)} onReleased={() => {
        setReleaseTarget(undefined)
        viewAlert({ severity: 'information', message: t('PREORDERS_RELEASE_STARTED') })
        reload()
      }} />}
    </HardwareListPage>
  )
}

export default DedicatedPreordersPage
